import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2, Plus, Send, Check, FileSignature, Trash2, Edit, Eye } from 'lucide-react';
import { quotes as quotesApi } from '@/lib/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const STATUS_TONE = {
  draft: 'border-slate-200 bg-slate-50 text-slate-700',
  sent: 'border-blue-200 bg-blue-50 text-blue-700',
  accepted: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  signed: 'border-emerald-300 bg-emerald-100 text-emerald-800',
  converted: 'border-purple-200 bg-purple-50 text-purple-700',
  declined: 'border-red-200 bg-red-50 text-red-700',
  cancelled: 'border-slate-200 bg-slate-50 text-slate-500',
};
const money = (v) => `A$${(v || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function QuotesPage() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [previewing, setPreviewing] = React.useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    const opts = statusFilter !== 'all' ? { status_filter: statusFilter } : {};
    quotesApi.list(opts).then((d) => setItems(d.items || []))
      .catch(() => toast.error('Failed to load quotes'))
      .finally(() => setLoading(false));
  }, [statusFilter]);
  React.useEffect(load, [load]);

  const doSend = async (id) => {
    try { const r = await quotesApi.send(id); toast.success(`Sent for signature to ${r.sent_to}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); }
  };
  const doConvert = async (id) => {
    try { const r = await quotesApi.convert(id); toast.success(`Matter ${r.matter_ref} created`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Convert failed'); }
  };
  const doMarkSigned = async (id) => {
    if (!window.confirm('Mark this quote as signed and auto-create the matter + first invoice?')) return;
    try { const r = await quotesApi.markSigned(id); toast.success(`Matter ${r.matter_ref} created`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const doDelete = async (id) => {
    if (!window.confirm('Delete this quote?')) return;
    try { await quotesApi.remove(id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="p-8" data-testid="quotes-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#1A3A5C]">Quote generator</h1>
          <p className="mt-1 text-sm text-slate-500">Service-catalogue quotes → eSignable engagement letters → auto-spawn matter + first invoice.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="quote-create-btn">
          <Plus className="mr-2 h-4 w-4" /> New quote
        </Button>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="quote-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="signed">Signed</SelectItem>
            <SelectItem value="converted">Converted</SelectItem>
            <SelectItem value="declined">Declined</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="mt-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>
      ) : items.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-sm text-slate-500">
          No quotes yet. Create one to send an engagement letter for signature.
        </CardContent></Card>
      ) : (
        <Card><CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Applicant</th>
                <th className="px-4 py-3">Subclass</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Matter</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((q) => (
                <tr key={q.id} className="border-b border-slate-50" data-testid={`quote-row-${q.id}`}>
                  <td className="px-4 py-3 font-medium text-[#1A3A5C]">{q.title}</td>
                  <td className="px-4 py-3 text-slate-700"><div>{q.applicant_name}</div><div className="text-xs text-slate-500">{q.applicant_email}</div></td>
                  <td className="px-4 py-3 text-slate-600">{q.subclass || '—'}</td>
                  <td className="px-4 py-3 font-semibold text-emerald-700">{money(q.total)}</td>
                  <td className="px-4 py-3"><Badge className={`${STATUS_TONE[q.status]} border`} data-testid={`quote-status-${q.id}`}>{q.status}</Badge></td>
                  <td className="px-4 py-3 text-xs">
                    {q.matter_ref ? <Link to={`/app/matters/${q.matter_id}`} className="text-[#2ECC9A] hover:underline">{q.matter_ref}</Link> : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setPreviewing(q)} data-testid={`quote-preview-${q.id}`}><Eye className="h-3 w-3" /></Button>
                      {q.status === 'draft' && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => doSend(q.id)} data-testid={`quote-send-${q.id}`}><Send className="mr-1 h-3 w-3" />Send</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditing(q)} data-testid={`quote-edit-${q.id}`}><Edit className="h-3 w-3" /></Button>
                        </>
                      )}
                      {q.status === 'sent' && (
                        <Button size="sm" variant="outline" onClick={() => doMarkSigned(q.id)} data-testid={`quote-mark-signed-${q.id}`}><Check className="mr-1 h-3 w-3" />Mark signed</Button>
                      )}
                      {q.status === 'signed' && !q.matter_id && (
                        <Button size="sm" onClick={() => doConvert(q.id)} data-testid={`quote-convert-${q.id}`}><FileSignature className="mr-1 h-3 w-3" />Convert to matter</Button>
                      )}
                      {q.status !== 'converted' && (
                        <Button size="sm" variant="ghost" onClick={() => doDelete(q.id)} data-testid={`quote-delete-${q.id}`}><Trash2 className="h-3 w-3 text-red-500" /></Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent></Card>
      )}

      <QuoteFormDialog
        open={createOpen || !!editing}
        quote={editing}
        onClose={() => { setCreateOpen(false); setEditing(null); }}
        onSaved={load}
      />
      <QuotePreviewDialog quote={previewing} onClose={() => setPreviewing(null)} />
    </div>
  );
}

const emptyItem = () => ({ description: '', quantity: 1, unit_amount: 0 });

function QuoteFormDialog({ open, quote, onClose, onSaved }) {
  const [form, setForm] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    if (open) setForm(quote || { title: '', applicant_name: '', applicant_email: '', subclass: '', line_items: [emptyItem()], notes: '', terms: '' });
  }, [open, quote]);

  const updateItem = (i, patch) => {
    const items = [...(form.line_items || [])];
    items[i] = { ...items[i], ...patch };
    setForm({ ...form, line_items: items });
  };
  const addItem = () => setForm({ ...form, line_items: [...(form.line_items || []), emptyItem()] });
  const removeItem = (i) => {
    const items = (form.line_items || []).filter((_, idx) => idx !== i);
    setForm({ ...form, line_items: items.length ? items : [emptyItem()] });
  };
  const total = (form.line_items || []).reduce((s, i) => s + (Number(i.quantity) || 1) * (Number(i.unit_amount) || 0), 0);

  const submit = async () => {
    if (!form.title?.trim() || !form.applicant_name?.trim() || !form.applicant_email?.trim()) {
      toast.error('Title, name and email are required'); return;
    }
    if (!(form.line_items || []).length || !(form.line_items || []).every((i) => i.description && Number(i.unit_amount) >= 0)) {
      toast.error('Every line item needs a description and amount'); return;
    }
    setBusy(true);
    try {
      const payload = {
        title: form.title, applicant_name: form.applicant_name,
        applicant_email: form.applicant_email, subclass: form.subclass || null,
        line_items: form.line_items.map((i) => ({
          description: i.description,
          quantity: Number(i.quantity) || 1,
          unit_amount: Number(i.unit_amount) || 0,
        })),
        notes: form.notes || null, terms: form.terms || null,
      };
      if (quote) await quotesApi.update(quote.id, payload);
      else await quotesApi.create(payload);
      toast.success('Quote saved');
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="quote-form-dialog">
        <DialogHeader><DialogTitle>{quote ? 'Edit quote' : 'New quote'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Title</label>
            <Input value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Subclass 189 engagement letter" data-testid="quote-form-title" />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Applicant name</label>
              <Input value={form.applicant_name || ''} onChange={(e) => setForm({ ...form, applicant_name: e.target.value })} data-testid="quote-form-name" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Applicant email</label>
              <Input type="email" value={form.applicant_email || ''} onChange={(e) => setForm({ ...form, applicant_email: e.target.value })} data-testid="quote-form-email" />
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Subclass</label>
              <Input value={form.subclass || ''} onChange={(e) => setForm({ ...form, subclass: e.target.value })} placeholder="189, 482, 500..." data-testid="quote-form-subclass" />
            </div>
          </div>
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Line items</label>
              <Button size="sm" variant="outline" onClick={addItem} data-testid="quote-form-add-item"><Plus className="mr-1 h-3 w-3" /> Add line</Button>
            </div>
            <div className="space-y-2">
              {(form.line_items || []).map((item, i) => (
                <div key={i} className="grid grid-cols-12 items-center gap-2" data-testid={`quote-form-item-${i}`}>
                  <Input className="col-span-6" value={item.description} onChange={(e) => updateItem(i, { description: e.target.value })} placeholder="Description" data-testid={`quote-form-item-desc-${i}`} />
                  <Input className="col-span-2" type="number" value={item.quantity} onChange={(e) => updateItem(i, { quantity: e.target.value })} placeholder="Qty" data-testid={`quote-form-item-qty-${i}`} />
                  <Input className="col-span-3" type="number" value={item.unit_amount} onChange={(e) => updateItem(i, { unit_amount: e.target.value })} placeholder="Unit A$" data-testid={`quote-form-item-unit-${i}`} />
                  <Button size="icon" variant="ghost" onClick={() => removeItem(i)} data-testid={`quote-form-item-del-${i}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
            </div>
            <div className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-right font-semibold text-emerald-700" data-testid="quote-form-total">Total: {money(total)}</div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Notes</label>
            <Textarea rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Terms &amp; conditions</label>
            <Textarea rows={4} value={form.terms || ''} onChange={(e) => setForm({ ...form, terms: e.target.value })} placeholder="Payment terms, cancellation, refund policy..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="quote-form-submit">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuotePreviewDialog({ quote, onClose }) {
  const [html, setHtml] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  React.useEffect(() => {
    if (!quote) return;
    setLoading(true);
    quotesApi.previewHtmlAuthed(quote.id)
      .then(setHtml)
      .catch(() => toast.error('Failed to load preview'))
      .finally(() => setLoading(false));
  }, [quote]);
  if (!quote) return null;
  return (
    <Dialog open={!!quote} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="quote-preview-dialog">
        <DialogHeader><DialogTitle>{quote.title}</DialogTitle></DialogHeader>
        <div className="rounded-lg border border-slate-200 bg-white">
          {loading ? (
            <div className="flex h-[520px] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>
          ) : (
            <iframe
              title="Quote preview"
              srcDoc={html}
              sandbox=""
              className="h-[520px] w-full rounded-lg"
              data-testid="quote-preview-frame"
            />
          )}
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
